import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { API_BASE_URL } from "../../config";
import { DMTG, OTHERS } from "../Home";
import useIsMountedRef from "../../utils/mountedRef";
import BadgeNouveau from "../../components/texts/BadgeNouveau";
import { NEWSIMIDS } from "../Home";

import "./Favoris.css";

function Favoris() {
  const history = useHistory();
  const isMountedRef = useIsMountedRef();
  const [simulateurs, setSimulateurs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allFavoris, setAllFavoris] = useState(() => {
    try {
      const storedFavoris = localStorage.getItem("favoris");
      if (typeof storedFavoris === "string" && storedFavoris.length > 0) {
        return JSON.parse(storedFavoris);
      }
    } catch (error) {
      return {};
    }
    return {};
  });

  useEffect(() => {
    const simulateursRequest = {
      method: "get",
      url: `${API_BASE_URL}/simulateurs`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };

    axios(simulateursRequest)
      .then((response) => {
        if (response.status === 200 && response.data != null && isMountedRef.current) {
          setSimulateurs(response.data);
        }
      })
      .catch((error) => {
        if (error.response?.status === 401 && error.response.data != null) {
          if (error.response.data.error === "Other_user_connected") {
            localStorage.removeItem("user");
            localStorage.setItem("other_user_connected", "other_user_connected");
            window.location.href = "/login";
          }
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  }, [isMountedRef]);

  const favoriteIds = useMemo(() => {
    if (!allFavoris || typeof allFavoris !== "object") {
      return [];
    }

    return Object.values(allFavoris).map((value) => value.toString());
  }, [allFavoris]);

  const favoriteSimulateurs = useMemo(
    () => simulateurs.filter((simulateur) => favoriteIds.includes(simulateur.id.toString())),
    [favoriteIds, simulateurs]
  );

  const getSimulateurPath = (simulateur) => {
    if (DMTG.includes(simulateur.id)) {
      return `/categorie/${simulateur.subcategorie[0].categorie.url_name}/dmtg/${simulateur.id}`;
    }

    if (OTHERS.includes(simulateur.id)) {
      return `/categorie/${simulateur.subcategorie[0].categorie.url_name}/others/${simulateur.id}`;
    }

    return `/categorie/${simulateur.subcategorie[0].categorie.url_name}/simulateur/${simulateur.id}`;
  };

  const toggleFavori = (id) => {
    const config = {
      method: "get",
      url: `${API_BASE_URL}/favoris/${id}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };

    axios(config).then((response) => {
      if (response.status === 200) {
        if (!response.data) {
          localStorage.removeItem("favoris");
          setAllFavoris({});
          return;
        }

        localStorage.setItem("favoris", response.data);
        setAllFavoris(JSON.parse(response.data));
      }
    });
  };

  const favoriteCountLabel = `${favoriteSimulateurs.length} ${
    favoriteSimulateurs.length > 1 ? "simulateurs" : "simulateur"
  }`;

  return (
    <div className="favoris-page">
      <div className="favoris-shell">
        <header className="favoris-hero">
          <div className="favoris-hero-copy">
            <h1>Mes favoris</h1>
            <p>Retrouvez rapidement les simulateurs que vous consultez le plus souvent.</p>
          </div>
          <div className="favoris-counter" aria-label={favoriteCountLabel}>
            <strong>{favoriteSimulateurs.length}</strong>
            <span>{favoriteSimulateurs.length > 1 ? "simulateurs" : "simulateur"}</span>
          </div>
        </header>

        {isLoading ? (
          <div className="favoris-status">Chargement de vos favoris...</div>
        ) : favoriteSimulateurs.length === 0 ? (
          <div className="favoris-empty">
            <div className="favoris-empty-mark">★</div>
            <div className="favoris-empty-content">
              <span className="favoris-empty-kicker">Votre sélection est vide</span>
              <h2>Gardez vos simulateurs utiles à portée de main</h2>
              <p>Sélectionnez vos outils de référence pour constituer un accès rapide adapté à vos usages.</p>
            </div>
            <button type="button" className="favoris-empty-action" onClick={() => history.push("/")}>
              Voir les simulateurs
            </button>
          </div>
        ) : (
          <div className={`favoris-grid ${favoriteSimulateurs.length === 1 ? "favoris-grid--single" : ""}`}>
            {favoriteSimulateurs.map((simulateur) => {
              const category = simulateur.subcategorie?.[0]?.categorie?.name || "Simulateur";
              const subcategory = simulateur.subcategorie?.[0]?.name;

              return (
                <article key={simulateur.id} className={`favoris-card ${favoriteSimulateurs.length === 1 ? "favoris-card--single" : ""}`}>
                  <div className="favoris-card-top">
                    <span className="favoris-card-category">{category}</span>
                    <button
                      type="button"
                      className="favoris-star"
                      aria-label={`Retirer ${simulateur.name} des favoris`}
                      onClick={() => toggleFavori(simulateur.id)}
                    >
                      ★
                    </button>
                  </div>

                  <div className="favoris-card-body">
                    <div className="favoris-card-title">
                      <h2>{simulateur.name}</h2>
                      {NEWSIMIDS.includes(simulateur.id) ? <BadgeNouveau cliquable={false} /> : null}
                    </div>
                    {subcategory ? <p className="favoris-card-meta">{subcategory}</p> : null}
                  </div>

                  <div className="favoris-card-actions">
                    <button
                      type="button"
                      className="favoris-open"
                      onClick={() => {
                        history.push(getSimulateurPath(simulateur));
                      }}
                    >
                      Accéder
                    </button>
                    <button type="button" className="favoris-remove" onClick={() => toggleFavori(simulateur.id)}>
                      Retirer des favoris
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Favoris;
